import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { fetchAllMetrics, fetchTestMetrics } from '../../../services/mongod.data';

// Initial State
const initialState = {
    metrics: [],
    testMetrics: {},
    metricsSummary: {},
    loading: false,
    error: null,
    lastFetched: null,
};

// ============================================================================
// ASYNC THUNKS (API Calls)
// ============================================================================

// Fetch all metrics for a shop
export const fetchAllMetricsapi = createAsyncThunk(
    'metrics/fetchAll',
    async ({ cf_username, forceRefresh = false }, { getState, rejectWithValue }) => {
        try {
            console.log('Metrics: Starting fetchAllMetricsapi...');
            const state = getState().metrics;
            // Check cache (refresh every 5 minutes)
            if (state.metrics.length > 0 && state.lastFetched && !forceRefresh) {
                const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
                if (new Date(state.lastFetched) > fiveMinutesAgo) {
                    console.log('Metrics: Using cached metrics');
                    return { fromCache: true, metrics: state.metrics };
                }
            }

            console.log('Metrics: Fetching from API...',cf_username);
            if (!cf_username) {
                return rejectWithValue('CF username is required');
            }

            const result = await fetchAllMetrics(cf_username);
            console.log('Metrics: API Response:', result);

            if (result.success && result.metrics) {
                return { 
                    fromCache: false, 
                    metrics: result.metrics,
                    count: result.count 
                };
            } else {
                return rejectWithValue(result.error || 'Failed to fetch metrics');
            }
        } catch (err) {
            console.error('Metrics: Error fetching metrics:', err);
            return rejectWithValue(err.message);
        }
    }
);

// Fetch metrics for specific test
export const fetchTestMetricsapi = createAsyncThunk(
    'metrics/fetchTestMetricsapi',
    async ({ cf_username, testId, filters = {} }, { rejectWithValue }) => {
        console.log('Metrics: Starting fetchTestMetricsapi...', { cf_username, testId, filters });
        try {
            if (!cf_username || !testId) {
                return rejectWithValue('CF username and test ID are required');
            }

            // const queryParams = new URLSearchParams({
            //     shop: cf_username,
            //     testId,
            //     ...filters
            // });

            // const response = await fetch(`/api/abtest/metrics?${queryParams.toString()}`, {
            //     method: 'GET',
            //     headers: {
            //         'Content-Type': 'application/json',
            //         'X-Shop-Username': cf_username
            //     }
            // });

            // if (!response.ok) {
            //     throw new Error(`Failed to fetch test metrics: ${response.status}`);
            // }

            const result = await fetchTestMetrics(cf_username, testId, filters);
            console.log(`Metrics: Test ${testId} metrics:`, result);

            if (result.success && result.metrics) {
                return { 
                    testId,
                    metrics: result.metrics, 
                    count: result.count 
                };
            } else {
                return rejectWithValue(result.error || 'Failed to fetch test metrics');
            }
        } catch (err) {
            console.error('Metrics: Error fetching test metrics:', err);
            return rejectWithValue(err.message);
        }
    }
);

// Fetch metrics summary for specific test
export const fetchTestSummary = createAsyncThunk(
    'metrics/fetchTestSummary',
    async ({ cf_username, testId }, { rejectWithValue }) => {
        try {
            if (!cf_username || !testId) {
                return rejectWithValue('CF username and test ID are required');
            }

            const formData = new FormData();
            formData.append('shopDomain', cf_username);
            formData.append('testId', testId);
            formData.append('action', 'summary');

            const response = await fetch('/api/abtest/metrics', {
                method: 'POST',
                headers: {
                    'X-Shop-Username': cf_username
                },
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch test summary: ${response.status}`);
            }

            const result = await response.json();
            console.log(`Metrics: Test ${testId} summary:`, result);

            if (result.success && result.summary) {
                return { 
                    testId,
                    summary: result.summary 
                };
            } else {
                return rejectWithValue(result.error || 'Failed to fetch test summary');
            }
        } catch (err) {
            console.error('Metrics: Error fetching test summary:', err);
            return rejectWithValue(err.message);
        }
    }
);

// ============================================================================
// SLICE
// ============================================================================

const metricsSlice = createSlice({
    name: 'metrics',
    initialState,
    reducers: {
        // Clear all metrics data
        clearMetrics: (state) => {
            state.metrics = [];
            state.testMetrics = {};
            state.metricsSummary = {};
            state.lastFetched = null;
            state.error = null;
        },
        
        // Set error
        setError: (state, action) => {
            state.error = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Fetch All Metrics
        builder
            .addCase(fetchAllMetricsapi.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAllMetricsapi.fulfilled, (state, action) => {
                state.loading = false;
                
                if (!action.payload.fromCache) {
                    // Group metrics by testId
                    const testMetrics = {};
                    action.payload.metrics.forEach(metric => {
                        if (!testMetrics[metric.testId]) {
                            testMetrics[metric.testId] = [];
                        }
                        testMetrics[metric.testId].push(metric);
                    });
                    
                    state.metrics = action.payload.metrics;
                    state.testMetrics = testMetrics;
                    state.lastFetched = new Date().toISOString();
                }
            })
            .addCase(fetchAllMetricsapi.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Fetch Test Metrics
        builder
            .addCase(fetchTestMetricsapi.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTestMetricsapi.fulfilled, (state, action) => {
                state.loading = false;
                state.testMetrics[action.payload.testId] = action.payload.metrics;
            })
            .addCase(fetchTestMetricsapi.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Fetch Test Summary
        builder
            .addCase(fetchTestSummary.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTestSummary.fulfilled, (state, action) => {
                state.loading = false;
                state.metricsSummary[action.payload.testId] = action.payload.summary;
            })
            .addCase(fetchTestSummary.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

// ============================================================================
// ACTIONS
// ============================================================================

export const { clearMetrics, setError } = metricsSlice.actions;

// ============================================================================
// SELECTORS
// ============================================================================

// Basic selectors
export const selectMetrics = (state) => state.metrics.metrics;
export const selectTestMetrics = (state) => state.metrics.testMetrics;
export const selectMetricsSummary = (state) => state.metrics.metricsSummary;
export const selectLoading = (state) => state.metrics.loading;
export const selectError = (state) => state.metrics.error;

// Get metrics for a specific test
export const selectTestMetricsById = (testId) => 
    createSelector(
        [selectTestMetrics],
        (testMetrics) => testMetrics[testId] || []
    );

// Get summary for a specific test
export const selectTestSummaryById = (testId) =>
    createSelector(
        [selectMetricsSummary],
        (metricsSummary) => metricsSummary[testId] || null
    );

// Get conversion funnel for a test
export const selectConversionFunnel = (testId) =>
    createSelector(
        [selectTestMetricsById(testId)],
        (metrics) => {
            const funnel = {
                control: {
                    views: 0,
                    element_applied: 0,
                    button_clicks: 0,
                    add_to_cart: 0,
                    purchases: 0
                },
                variant: {
                    views: 0,
                    element_applied: 0,
                    button_clicks: 0,
                    add_to_cart: 0,
                    purchases: 0
                }
            };

            metrics.forEach(metric => {
                const group = metric.group || 'unknown';
                if (funnel[group]) {
                    switch (metric.event) {
                        case 'view':
                            funnel[group].views++;
                            break;
                        case 'element_applied':
                            funnel[group].element_applied++;
                            break;
                        case 'button_click':
                            funnel[group].button_clicks++;
                            break;
                        case 'add_to_cart':
                            funnel[group].add_to_cart++;
                            break;
                        case 'purchase':
                            funnel[group].purchases++;
                            break;
                    }
                }
            });

            // Calculate conversion rates
            const calculateRates = (group) => ({
                ...group,
                click_rate: group.views > 0 ? (group.button_clicks / group.views * 100).toFixed(2) : '0.00',
                cart_rate: group.button_clicks > 0 ? (group.add_to_cart / group.button_clicks * 100).toFixed(2) : '0.00',
                purchase_rate: group.views > 0 ? (group.purchases / group.views * 100).toFixed(2) : '0.00'
            });

            return {
                control: calculateRates(funnel.control),
                variant: calculateRates(funnel.variant)
            };
        }
    );

// Get daily metrics breakdown
export const selectDailyMetrics = (testId, days = 7) =>
    createSelector(
        [selectTestMetricsById(testId)],
        (metrics) => {
            const daily = {};
            
            // Initialize days
            for (let i = days - 1; i >= 0; i--) {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateStr = date.toISOString().split('T')[0];
                daily[dateStr] = {
                    control: { views: 0, clicks: 0, purchases: 0 },
                    variant: { views: 0, clicks: 0, purchases: 0 }
                };
            }

            // Populate with actual data
            metrics.forEach(metric => {
                const date = new Date(metric.timestamp).toISOString().split('T')[0];
                if (daily[date] && daily[date][metric.group]) {
                    switch (metric.event) {
                        case 'view':
                            daily[date][metric.group].views++;
                            break;
                        case 'button_click':
                            daily[date][metric.group].clicks++;
                            break;
                        case 'purchase':
                            daily[date][metric.group].purchases++;
                            break;
                    }
                }
            });

            return daily;
        }
    );

// Get real-time metrics summary for all tests
export const selectRealtimeSummary = createSelector(
    [selectTestMetrics],
    (testMetrics) => {
        const summary = {};
        
        Object.keys(testMetrics).forEach(testId => {
            const metrics = testMetrics[testId];
            const today = new Date().toISOString().split('T')[0];
            const todayMetrics = metrics.filter(metric =>
                metric.timestamp.startsWith(today)
            );

            summary[testId] = {
                total_events: metrics.length,
                today_events: todayMetrics.length,
                last_event: metrics.length > 0 ? metrics[metrics.length - 1].timestamp : null,
                control_views: metrics.filter(m => m.group === 'control' && m.event === 'view').length,
                variant_views: metrics.filter(m => m.group === 'variant' && m.event === 'view').length,
                total_clicks: metrics.filter(m => m.event === 'button_click').length
            };
        });

        return summary;
    }
);

// Get event breakdown for a test
export const selectEventBreakdown = (testId) =>
    createSelector(
        [selectTestMetricsById(testId)],
        (metrics) => {
            const breakdown = {};
            
            metrics.forEach(metric => {
                const key = `${metric.group}_${metric.event}`;
                breakdown[key] = (breakdown[key] || 0) + 1;
            });

            return breakdown;
        }
    );

// Get metrics by date range
export const selectMetricsByDateRange = (testId, startDate, endDate) =>
    createSelector(
        [selectTestMetricsById(testId)],
        (metrics) => {
            const start = new Date(startDate);
            const end = new Date(endDate);
            
            return metrics.filter(metric => {
                const metricDate = new Date(metric.timestamp);
                return metricDate >= start && metricDate <= end;
            });
        }
    );

// ============================================================================
// REDUCER (Default Export)
// ============================================================================

export default metricsSlice.reducer;